import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, Phone, Mail, MapPin, ArrowLeft, Cpu, Check } from "lucide-react";
import { MACHINES, COMPANY } from "@/data/content";

// Print-optimised brochure — one machine per document.
// Users click Print/Save-as-PDF (native browser dialog) to download.
const Brochure = () => {
  const { id } = useParams();
  const m = MACHINES.find((x) => x.id === id) || MACHINES[0];

  useEffect(() => {
    document.title = `${m.name} — Brochure | ${COMPANY.fullName}`;
  }, [m]);

  return (
    <div className="min-h-screen bg-white text-slate-900 pt-24">
      {/* Toolbar — hidden on print */}
      <div className="no-print sticky top-20 z-30 border-y border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-[900px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to={`/machines/${m.id}`} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors" data-testid="brochure-back">
            <ArrowLeft className="w-4 h-4" /> Back to machine
          </Link>
          <button onClick={() => window.print()} className="btn-primary !py-2.5 !px-5 !text-sm" data-testid="brochure-print-btn">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Brochure document */}
      <article className="max-w-[900px] mx-auto px-8 md:px-12 py-10 print:py-0 print:px-6" data-testid="brochure-doc">
        {/* Header — company branding */}
        <header className="flex items-start justify-between pb-6 border-b border-slate-300">
          <div>
            <p className="font-display text-3xl">
              <span className="text-[#0055FF]">Accurate</span>
              <span className="text-slate-500 text-lg ml-2 font-medium">Automation & Reconditioners</span>
            </p>
            <p className="mt-1 text-xs text-slate-500 uppercase tracking-widest">Machine Specification Brochure</p>
          </div>
          <div className="text-right text-xs text-slate-600 space-y-1">
            <p className="flex items-center justify-end gap-1.5"><Phone className="w-3 h-3 text-[#0055FF]" /> {COMPANY.phone}</p>
            <p className="flex items-center justify-end gap-1.5"><Mail className="w-3 h-3 text-[#0055FF]" /> {COMPANY.email}</p>
          </div>
        </header>

        {/* Machine title */}
        <section className="mt-8">
          <p className="text-xs uppercase tracking-widest text-[#0055FF]">{m.type}</p>
          <h1 className="mt-2 font-display text-4xl md:text-5xl leading-tight text-slate-900">{m.name}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="px-3 py-1 rounded-full text-xs bg-[#0055FF] text-white font-medium">{m.controller}</span>
            <span className="px-3 py-1 rounded-full text-xs bg-slate-100 border border-slate-300 text-slate-700">{m.condition}</span>
          </div>
        </section>

        {/* Hero image */}
        <section className="mt-8">
          <div className="w-full h-[280px] md:h-[360px] rounded-2xl overflow-hidden border border-slate-200">
            <img src={m.image} alt={m.name} className="w-full h-full object-cover" />
          </div>
        </section>

        {/* Specifications */}
        <section className="mt-10">
          <h2 className="font-display text-2xl text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-300">
            <Cpu className="w-5 h-5 text-[#0055FF]" /> Technical Specifications
          </h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {Object.entries(m.specs).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-2 border-b border-slate-100">
                <span className="text-sm text-slate-600">{k}</span>
                <span className="text-sm font-semibold text-slate-900 text-right">{v}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        {m.features?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl text-slate-900 pb-2 border-b border-slate-300">Key Features</h2>
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {m.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-[#0055FF] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Company contact */}
        <section className="mt-10 pt-6 border-t border-slate-300">
          <h2 className="font-display text-xl text-slate-900">For enquiries & quotation</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm text-slate-700">
            <div className="space-y-1.5">
              <p className="flex items-start gap-2"><Phone className="w-4 h-4 text-[#0055FF] mt-0.5" /> Office: {COMPANY.officePhone}</p>
              <p className="flex items-start gap-2"><Phone className="w-4 h-4 text-[#0055FF] mt-0.5" /> WhatsApp: {COMPANY.phone}</p>
              <p className="flex items-start gap-2"><Mail className="w-4 h-4 text-[#0055FF] mt-0.5" /> {COMPANY.email}</p>
            </div>
            <div>
              <p className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#0055FF] mt-0.5 shrink-0" /> {COMPANY.address}</p>
            </div>
          </div>
        </section>

        <footer className="mt-10 pt-4 border-t border-slate-200 text-[10px] text-slate-400 uppercase tracking-widest text-center">
          © {new Date().getFullYear()} {COMPANY.legal} · All specifications subject to change without notice · Photograph is representative
        </footer>
      </article>
    </div>
  );
};

export default Brochure;
